/**
=========================================================
* Argon Dashboard 2 PRO MUI - v3.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-pro-mui
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { forwardRef, useState } from 'react';

// prop-types is a library for typechecking of props
import PropTypes from 'prop-types';

// Custom styles for ArgonInput
import ArgonInputRoot from 'components/ArgonCurrencyInput/ArgonInputRoot';
import InputBase from '@mui/material/InputBase';

// Argon Dashboard 2 PRO MUI context
import { useArgonController } from 'context';
import { Input, TextField } from '@mui/material';
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import ArgonTextFieldRoot from 'components/ArgonTextFieldSelect/ArgonTextFieldRoot';
import ArgonInput from 'components/ArgonInput';

/**
 * FormikCurrencyInput Komponente
 *
 * Diese Komponente ermöglicht die Eingabe von Währungsbeträgen mit folgenden Eigenschaften:
 * - Verwendet einen lokalen State für temporäre Werte während der Eingabe
 * - Behält den Dezimaltrenner während der Eingabe bei
 */
const FormikCurrencyInput = forwardRef(
  (
    {
      id,
      name,
      label,
      formik,
      size,
      required,
      error,
      success,
      helperText,
      disabled,
      width,
      style,
      language,
      currency,
      ...rest
    },
    ref
  ) => {
    const [controller] = useArgonController();
    const { darkMode } = controller;

    // Lokaler State für den temporären Wert während der Eingabe
    const [tempInputValue, setTempInputValue] = useState(null);

    const handleValueChanged = (value) => {
      console.log('FormikCurrencyInput', name, 'changed:', value);
      formik.setFieldValue(name, Number(value));
    };

    let originalFieldName = name;

    return (
      <>
        {label && (
          <>
            <ArgonBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
              <ArgonTypography
                component="label"
                variant="caption"
                fontWeight="bold"
                textTransform="capitalize">
                {label}
                {required && <span style={{ color: 'red' }}>&nbsp;*</span>}
              </ArgonTypography>
            </ArgonBox>
          </>
        )}
        <ArgonInputRoot
          id={id + '-currency-input'}
          // name={name + '-currency-input'}
          intlConfig={{
            locale: language || 'de',
            currency: currency || 'EUR'
          }}
          // prefix={(currency || 'EUR') + ' '}
          decimalSeparator=","
          groupSeparator="."
          // prefix={currency || 'EUR' + ' '}
          allowNegativeValue={false}
          placeholder=""
          // defaultValue={0}
          decimalsLimit={2}
          decimalScale={2}
          // fixedDecimalLength={2}
          customInput={ArgonInput}
          // success={success}
          error={
            error || (formik.touched?.[name] && Boolean(formik.errors?.[name]))
          }
          helperText={
            helperText || (formik.touched?.[name] && formik.errors?.[name])
          }
          value={
            // Wenn ein temporärer Wert im lokalen State existiert, verwenden wir diesen
            tempInputValue !== null
              ? tempInputValue
              : formik.values?.[name] === null ||
                formik.values?.[name] === undefined
              ? ''
              : formik.values?.[name]
          }
          // Diese Implementierung löst das Problem mit dem verschwindenden Dezimaltrenner
          // während der Eingabe, indem ein lokaler State verwendet wird, um den
          // Originalwert als String zu speichern, wenn er mit einem Dezimaltrenner endet.
          onValueChange={(value, name, values) => {
            console.log('FormikCurrencyInput', name, 'changed:', value, values);

            // Wenn der Wert undefined, null oder leer ist
            if (value === undefined || value === null || value === '') {
              formik.setFieldValue(originalFieldName, null);
              setTempInputValue(null);
              return;
            }

            // Prüfen, ob der Wert mit einem Dezimaltrenner endet
            const endsWithDecimalSeparator = value.toString().endsWith(',');

            if (endsWithDecimalSeparator) {
              // Wenn der Wert mit einem Dezimaltrenner endet, speichern wir den Originalwert
              // im lokalen State, um ihn beim nächsten Rendering zu erhalten
              setTempInputValue(value);

              // Für Formik-Validierung speichern wir trotzdem einen numerischen Wert
              // (ohne den Dezimaltrenner am Ende)
              const numericValue = parseFloat(value.replace(',', '.'));
              formik.setFieldValue(originalFieldName, numericValue);
            } else {
              // Wenn der Wert nicht mit einem Dezimaltrenner endet, setzen wir den temporären Wert zurück
              setTempInputValue(null);

              // Normaler Fall: Wert als Zahl speichern
              // Ersetze Komma durch Punkt für korrekte Umwandlung
              const numericValue = parseFloat(value.replace(',', '.'));
              formik.setFieldValue(originalFieldName, numericValue);
            }
          }}
          {...rest}
          ref={ref}
          ownerState={{ size, error, success, disabled, darkMode, width }}
          style={style}
        />
        {helperText ||
          (formik.touched?.[name] && formik.errors?.[name] && (
            <>
              <ArgonBox mt={-1.4} ml={0.5}>
                <ArgonTypography
                  component="label"
                  color="error"
                  variant="caption"
                  fontWeight="regular"
                  textTransform="capitalize">
                  {helperText || formik.errors?.[name]}
                </ArgonTypography>
              </ArgonBox>
            </>
          ))}
      </>
    );
  }
);

// Setting default values for the props of ArgonInput
FormikCurrencyInput.defaultProps = {
  size: 'medium',
  error: false,
  success: false,
  disabled: false
};

// Typechecking props for the ArgonInput
FormikCurrencyInput.propTypes = {
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  error: PropTypes.bool,
  success: PropTypes.bool,
  disabled: PropTypes.bool
};

export default FormikCurrencyInput;
